// BillHistory.jsx - Ant Design UI
import React, { useEffect, useState, useMemo } from "react";
import {
  Table,
  DatePicker,
  Select,
  Input,
  Button,
  Card,
  Tabs,
  Space,
  Tag,
  Statistic,
  Row,
  Col,
  Typography,
  Tooltip as AntTooltip,
  Badge,
} from "antd";
import {
  SearchOutlined,
  ClearOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
  EyeOutlined,
  ArrowLeftOutlined,
  HomeOutlined,
  FileTextOutlined,
  BarChartOutlined,
} from "@ant-design/icons";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "antd/dist/reset.css";
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
} from "recharts";

import Layout from "../../layout/Layout";
import Header from "../../components/Header";
import fetchData from "../../functions/fetchData";
import logo from "../../assets/logo.png";
import BillItemModal from "../../components/Modals/BillItemModal";
import { getUserType } from "../../utility/auth";

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Title, Text } = Typography;

const CHART_COLORS = [
  "#3498db","#e74c3c","#2ecc71","#f39c12","#9b59b6",
  "#1abc9c","#34495e","#e67e22","#95a5a6","#f1c40f","#8e44ad","#16a085",
];

const PAYMENT_METHODS = [
  { value: "cash", label: "Cash" },
  { value: "upi", label: "UPI" },
  { value: "card", label: "Card" },
  { value: "qr", label: "QR Code" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "online", label: "Online" },
  { value: "entertainment", label: "Entertainment" },
  { value: "other", label: "Other" },
];

const PAYMENT_TAG_COLOR = {
  cash: "green",
  upi: "blue",
  card: "geekblue",
  qr: "cyan",
  bank_transfer: "orange",
  online: "purple",
  entertainment: "magenta",
  other: "default",
};

export default function BillHistory() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState(() => {
    const s = localStorage.getItem("startDate");
    const e = localStorage.getItem("endDate");
    if (s && e) return [dayjs(s), dayjs(e)];
    return null;
  });
  const [paymentMode, setPaymentMode] = useState(
    localStorage.getItem("paymentMode") || ""
  );
  const [orderNoSearch, setOrderNoSearch] = useState("");
  const [customerSearch, setCustomerSearch] = useState("");
  const [cashierSearch, setCashierSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [activeTab, setActiveTab] = useState("active");

  const navigate = useNavigate();
  const userType = getUserType();
  const isCashier = userType?.toLowerCase() === "cashier";

  // ── Fetch ────────────────────────────────────────────────────────────────
  const loadData = async () => {
    setLoading(true);
    try {
      await fetchData("bills", setData, "id", {});
    } catch (_) {
      toast.error("Failed to fetch bills");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Persist filters
  useEffect(() => {
    if (dateRange) {
      localStorage.setItem("startDate", dateRange[0].format("YYYY-MM-DD"));
      localStorage.setItem("endDate", dateRange[1].format("YYYY-MM-DD"));
    } else {
      localStorage.removeItem("startDate");
      localStorage.removeItem("endDate");
    }
    localStorage.setItem("paymentMode", paymentMode);
  }, [dateRange, paymentMode]);

  // ── Filter logic (bill_date is the authoritative date column) ────────────
  const filteredData = useMemo(() => {
    let filtered = [...data];

    // 1. Status tab
    if (activeTab === "cancelled") {
      filtered = filtered.filter(
        (item) => item.status === "cancelled" || item.status === "refunded"
      );
    } else {
      filtered = filtered.filter((item) => item.status === "active");
    }

    // 2. Date range – using setup_date
    if (dateRange && dateRange[0] && dateRange[1]) {
      const startStr = dateRange[0].format("YYYY-MM-DD");
      const endStr = dateRange[1].format("YYYY-MM-DD");
      filtered = filtered.filter((item) => {
        if (!item.setup_date) return false;
        const d = item.setup_date.slice(0, 10); // "YYYY-MM-DD"
        return d >= startStr && d <= endStr;
      });
    }

    // 3. Payment method
    if (paymentMode) {
      filtered = filtered.filter(
        (item) => item.payment_method === paymentMode
      );
    } else {
      // Exclude entertainment unless explicitly selected
      filtered = filtered.filter(
        (item) => item.payment_method !== "entertainment"
      );
    }

    // 4. Bill number search
    if (orderNoSearch.trim()) {
      const q = orderNoSearch.trim().toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.bill_number && item.bill_number.toLowerCase().includes(q)
      );
    }

    // 5. Customer / phone search
    if (customerSearch.trim()) {
      const q = customerSearch.trim().toLowerCase();
      filtered = filtered.filter(
        (item) =>
          (item.customer_name &&
            item.customer_name.toLowerCase().includes(q)) ||
          (item.customer_phone && item.customer_phone.includes(q))
      );
    }

    // 6. Cashier ID search
    if (cashierSearch.trim()) {
      const q = cashierSearch.trim().toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.cashier_id &&
          String(item.cashier_id).toLowerCase().includes(q)
      );
    }

    // 7. Default sort: setup_date DESC, bill_time DESC
    filtered.sort((a, b) => {
      const da =
        (a.setup_date || "0000-00-00") + " " + (a.bill_time || "00:00:00");
      const db =
        (b.setup_date || "0000-00-00") + " " + (b.bill_time || "00:00:00");
      return db.localeCompare(da);
    });

    return filtered;
  }, [data, activeTab, dateRange, paymentMode, orderNoSearch, customerSearch, cashierSearch]);

  // ── Summary stats (computed from filteredData) ───────────────────────────
  const stats = useMemo(() => {
    const total = filteredData.reduce(
      (acc, item) => acc + parseFloat(item.total_amount || 0),
      0
    );
    const subtotal = filteredData.reduce(
      (acc, item) => acc + parseFloat(item.subtotal || 0),
      0
    );
    const tax = filteredData.reduce(
      (acc, item) => acc + parseFloat(item.tax_amount || 0),
      0
    );
    const discount = filteredData.reduce(
      (acc, item) => acc + parseFloat(item.discount_amount || 0),
      0
    );
    return {
      count: filteredData.length,
      total: total.toFixed(2),
      subtotal: subtotal.toFixed(2),
      tax: tax.toFixed(2),
      discount: discount.toFixed(2),
    };
  }, [filteredData]);

  // ── Monthly chart – uses setup_date ───────────────────────────────────────
  const monthlyData = useMemo(() => {
    const summary = {};
    filteredData.forEach((item) => {
      if (!item.setup_date) return;
      try {
        const month = format(new Date(item.setup_date), "MMM yyyy");
        summary[month] =
          (summary[month] || 0) + parseFloat(item.total_amount || 0);
      } catch (_) {}
    });
    return Object.entries(summary)
      .map(([month, total]) => ({ month, total }))
      .sort((a, b) => new Date("01 " + a.month) - new Date("01 " + b.month));
  }, [filteredData]);

  // ── Clear filters ────────────────────────────────────────────────────────
  const clearFilters = () => {
    setDateRange(null);
    setPaymentMode("");
    setOrderNoSearch("");
    setCustomerSearch("");
    setCashierSearch("");
    localStorage.removeItem("startDate");
    localStorage.removeItem("endDate");
    localStorage.removeItem("paymentMode");
  };

  // ── PDF export ───────────────────────────────────────────────────────────
  const exportPDF = () => {
    const doc = new jsPDF("landscape");
    doc.addImage(logo, "PNG", 200, 10, 40, 15);
    doc.setFontSize(16);
    doc.text("Bill History Report", 14, 20);

    const cols = [
      "Bill No.", "Date", "Time", "Customer",
      "Subtotal", "Tax", "Discount", "Total", "Payment",
    ];

    let tSub = 0, tTax = 0, tDis = 0, tTot = 0;
    const rows = filteredData.map((item) => {
      const sub = parseFloat(item.subtotal) || 0;
      const tax = parseFloat(item.tax_amount) || 0;
      const dis = parseFloat(item.discount_amount) || 0;
      const tot = parseFloat(item.total_amount) || 0;
      tSub += sub; tTax += tax; tDis += dis; tTot += tot;
      return [
        item.bill_number || "",
        item.bill_date ? item.bill_date.slice(0, 10) : "",
        item.bill_time || "",
        item.customer_name || "Walk-in",
        sub.toFixed(2),
        tax.toFixed(2),
        dis.toFixed(2),
        tot.toFixed(2),
        item.payment_method || "",
      ];
    });

    rows.push([
      { content: "TOTAL", colSpan: 4, styles: { halign: "right", fontStyle: "bold" } },
      tSub.toFixed(2), tTax.toFixed(2), tDis.toFixed(2), tTot.toFixed(2), "",
    ]);

    doc.autoTable({ startY: 30, head: [cols], body: rows, styles: { fontSize: 9 } });
    doc.save("bill_history_report.pdf");
  };

  // ── Table columns ────────────────────────────────────────────────────────
  const columns = [
    {
      title: "Cashier ID",
      dataIndex: "cashier_id",
      key: "cashier_id",
      width: 100,
      render: (val) => val || "-",
    },
    {
      title: "Bill No.",
      dataIndex: "bill_number",
      key: "bill_number",
      width: 155,
      render: (val, row) => (
        <Button
          type="link"
          style={{ padding: 0 }}
          onClick={() => { setSelectedBill(row); setShowModal(true); }}
        >
          {val}
        </Button>
      ),
    },
    {
      title: "Date",
      dataIndex: "setup_date",
      key: "setup_date",
      width: 110,
      sorter: (a, b) =>
        (a.setup_date || "").localeCompare(b.setup_date || ""),
      render: (val) => (val ? val.slice(0, 10) : "-"),
    },
    {
      title: "Time",
      dataIndex: "bill_time",
      key: "bill_time",
      width: 90,
      render: (val) => val || "-",
    },
    {
      title: "Customer",
      dataIndex: "customer_name",
      key: "customer_name",
      render: (val) => val || <Text type="secondary">Walk-in</Text>,
    },
    {
      title: "Subtotal (฿)",
      dataIndex: "subtotal",
      key: "subtotal",
      width: 115,
      align: "right",
      sorter: (a, b) => parseFloat(a.subtotal || 0) - parseFloat(b.subtotal || 0),
      render: (val) => parseFloat(val || 0).toFixed(2),
    },
    {
      title: "Tax (฿)",
      dataIndex: "tax_amount",
      key: "tax_amount",
      width: 90,
      align: "right",
      render: (val) => parseFloat(val || 0).toFixed(2),
    },
    {
      title: "Discount (฿)",
      dataIndex: "discount_amount",
      key: "discount_amount",
      width: 115,
      align: "right",
      render: (val) => parseFloat(val || 0).toFixed(2),
    },
    {
      title: "Total (฿)",
      dataIndex: "total_amount",
      key: "total_amount",
      width: 110,
      align: "right",
      sorter: (a, b) =>
        parseFloat(a.total_amount || 0) - parseFloat(b.total_amount || 0),
      render: (val) => (
        <Text strong style={{ color: "#1890ff" }}>
          {parseFloat(val || 0).toFixed(2)}
        </Text>
      ),
    },
    {
      title: "Payment",
      dataIndex: "payment_method",
      key: "payment_method",
      width: 130,
      render: (val) =>
        val ? (
          <Tag color={PAYMENT_TAG_COLOR[val] || "default"}>
            {val.toUpperCase()}
          </Tag>
        ) : "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (val) => {
        const color =
          val === "active"
            ? "success"
            : val === "cancelled"
            ? "error"
            : "warning";
        return <Badge status={color} text={val} />;
      },
    },
    {
      title: "Action",
      key: "actions",
      width: 80,
      fixed: "right",
      render: (_, row) => (
        <AntTooltip title="View Details">
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => { setSelectedBill(row); setShowModal(true); }}
          />
        </AntTooltip>
      ),
    },
  ];

  const tabItems = [
    {
      key: "active",
      label: (
        <span>
          <FileTextOutlined /> Active Bills
        </span>
      ),
    },
    {
      key: "cancelled",
      label: (
        <span style={{ color: "#ff4d4f" }}>
          <FileTextOutlined /> Cancelled / Refunded
        </span>
      ),
    },
  ];

  const renderContent = () => (
    <div style={{ padding: "0 8px" }}>
      <ToastContainer />

      {/* Cashier nav header */}
      {isCashier && (
        <div
          style={{
            background: "#343a40",
            color: "#fff",
            padding: "12px 20px",
            marginBottom: 16,
            borderRadius: 6,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              ghost
              size="small"
              onClick={() => navigate(-1)}
            >
              Back
            </Button>
            <Title level={4} style={{ margin: 0, color: "#fff" }}>
              Sales Reports
            </Title>
          </Space>
          <Button
            icon={<HomeOutlined />}
            ghost
            size="small"
            onClick={() => navigate("/dashboard")}
          >
            Dashboard
          </Button>
        </div>
      )}

      {/* ── Summary stat cards ── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}>
          <Card size="small" bordered style={{ background: "#e6f7ff" }}>
            <Statistic
              title="Total Records"
              value={stats.count}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" bordered style={{ background: "#f6ffed" }}>
            <Statistic
              title={activeTab === "cancelled" ? "Cancelled Total" : "Total Sale"}
              value={stats.total}
              prefix="฿"
              precision={2}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" bordered style={{ background: "#fffbe6" }}>
            <Statistic
              title="Total Tax"
              value={stats.tax}
              prefix="฿"
              precision={2}
              valueStyle={{ color: "#faad14" }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" bordered style={{ background: "#fff1f0" }}>
            <Statistic
              title="Total Discount"
              value={stats.discount}
              prefix="฿"
              precision={2}
              valueStyle={{ color: "#ff4d4f" }}
            />
          </Card>
        </Col>
      </Row>

      {/* ── Filter card ── */}
      <Card
        size="small"
        style={{ marginBottom: 16 }}
        title={
          <Space>
            <SearchOutlined />
            <span>Filters</span>
          </Space>
        }
        extra={
          <Button icon={<ClearOutlined />} size="small" onClick={clearFilters}>
            Clear All
          </Button>
        }
      >
        <Row gutter={[12, 12]} align="bottom">
          {/* Date range filter – uses bill_date column */}
          <Col xs={24} sm={12} md={8} lg={7}>
            <div>
              <Text type="secondary" style={{ display: "block", marginBottom: 4 }}>
                Date Range{" "}
                <Text type="danger" style={{ fontSize: 11 }}>
                  (setup_date)
                </Text>
              </Text>
              <RangePicker
                style={{ width: "100%" }}
                value={dateRange}
                onChange={(val) => setDateRange(val)}
                format="YYYY-MM-DD"
                allowClear
              />
            </div>
          </Col>

          {/* Payment method */}
          <Col xs={24} sm={12} md={5} lg={4}>
            <div>
              <Text type="secondary" style={{ display: "block", marginBottom: 4 }}>
                Payment Method
              </Text>
              <Select
                style={{ width: "100%" }}
                placeholder="All"
                value={paymentMode || undefined}
                allowClear
                onChange={(val) => setPaymentMode(val || "")}
              >
                {PAYMENT_METHODS.map((m) => (
                  <Option key={m.value} value={m.value}>
                    {m.label}
                  </Option>
                ))}
              </Select>
            </div>
          </Col>

          {/* Bill number */}
          <Col xs={24} sm={12} md={5} lg={4}>
            <div>
              <Text type="secondary" style={{ display: "block", marginBottom: 4 }}>
                Bill Number
              </Text>
              <Input
                placeholder="Search bill no."
                prefix={<SearchOutlined />}
                value={orderNoSearch}
                onChange={(e) => setOrderNoSearch(e.target.value)}
                allowClear
              />
            </div>
          </Col>

          {/* Customer / phone */}
          <Col xs={24} sm={12} md={6} lg={5}>
            <div>
              <Text type="secondary" style={{ display: "block", marginBottom: 4 }}>
                Customer / Phone
              </Text>
              <Input
                placeholder="Name or phone"
                prefix={<SearchOutlined />}
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                allowClear
              />
            </div>
          </Col>

          {/* Cashier ID */}
          <Col xs={24} sm={12} md={4} lg={3}>
            <div>
              <Text type="secondary" style={{ display: "block", marginBottom: 4 }}>
                Cashier ID
              </Text>
              <Input
                placeholder="Cashier ID"
                prefix={<SearchOutlined />}
                value={cashierSearch}
                onChange={(e) => setCashierSearch(e.target.value)}
                allowClear
              />
            </div>
          </Col>

          {/* Export buttons */}
          <Col xs={24} sm={24} md={24} lg={4}>
            <Space wrap>
              <CSVLink data={filteredData} filename="bill_history_report.csv">
                <Button
                  icon={<FileExcelOutlined />}
                  style={{ color: "#52c41a", borderColor: "#52c41a" }}
                >
                  CSV
                </Button>
              </CSVLink>
              <Button icon={<FilePdfOutlined />} danger onClick={exportPDF}>
                PDF
              </Button>
              <Button
                icon={<BarChartOutlined />}
                onClick={loadData}
                loading={loading}
              >
                Refresh
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* ── Tabs + Table ── */}
      <Card size="small">
        <Tabs
          activeKey={activeTab}
          items={tabItems}
          onChange={setActiveTab}
          style={{ marginBottom: 8 }}
        />

        <Table
          rowKey="id"
          loading={loading}
          dataSource={filteredData}
          columns={columns}
          scroll={{ x: 1150 }}
          size="small"
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            pageSizeOptions: ["15", "30", "50", "100"],
            showTotal: (total) => `Total ${total} records`,
            showLessItems: true,
          }}
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row
                style={{ background: "#fafafa", fontWeight: "bold" }}
              >
                <Table.Summary.Cell index={0} colSpan={5}>
                  <Text strong>TOTAL — {stats.count} records</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5} align="right">
                  <Text strong>฿{stats.subtotal}</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={6} align="right">
                  <Text strong>฿{stats.tax}</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={7} align="right">
                  <Text strong>฿{stats.discount}</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={8} align="right">
                  <Text strong style={{ color: "#1890ff" }}>
                    ฿{stats.total}
                  </Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={9} colSpan={3} />
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
      </Card>

      {/* ── Monthly sales bar chart ── */}
      {monthlyData.length > 0 && (
        <Card
          size="small"
          style={{ marginTop: 16 }}
          title={
            <Space>
              <BarChartOutlined />
              <span>Monthly Sales Summary</span>
              <Text type="secondary" style={{ fontSize: 12 }}>
                (based on setup_date)
              </Text>
            </Space>
          }
        >
          <ResponsiveContainer width="100%" height={280}>
            <BarChart
              data={monthlyData}
              margin={{ top: 8, right: 20, left: 10, bottom: 8 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip
                formatter={(value) => [`฿${value.toFixed(2)}`, "Total"]}
              />
              <Bar dataKey="total" radius={[4, 4, 0, 0]}>
                {monthlyData.map((_, i) => (
                  <Cell
                    key={i}
                    fill={CHART_COLORS[i % CHART_COLORS.length]}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* ── Bill detail modal ── */}
      {showModal && (
        <BillItemModal
          isOpen={showModal}
          bill={selectedBill}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );

  return isCashier ? (
    <div style={{ minHeight: "100vh", background: "#f0f2f5", padding: 16 }}>
      {renderContent()}
    </div>
  ) : (
    <Layout>
      <Header title="Sale Report" />
      {renderContent()}
    </Layout>
  );
}
